import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '@/app/hooks';
import { openAccountModal, type AccountModalPrefill } from '@/features/user/accountModalSlice';
import { useGuestProgram } from '../useGuestLoyalty';
import '../loyalty.css';

interface Props {
  restaurantUuid: string;
  /** The order's contact details: the sign-up form opens already filled. */
  prefill: AccountModalPrefill;
  /** A table (dine-in) order has no loyalty. */
  skip?: boolean;
}

/**
 * SC-1091 — on the confirmation of a GUEST order: create an account to earn points
 * from the next one. Honest by construction: points of orders placed without an
 * account are not credited afterwards, and the text says so — nothing implies THIS
 * order will be.
 */
export default function GuestConfirmCta({ restaurantUuid, prefill, skip }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();
  const program = useGuestProgram(restaurantUuid, skip);
  if (skip || !program?.enabled) return null;

  return (
    <section
      data-testid="guest-confirm-cta"
      className="loyalty-scope rounded-btn border-2 border-solid border-ink bg-paper p-4 shadow-stamp"
    >
      <p className="m-0 font-display text-lg text-ink">
        <span aria-hidden="true">🎁 </span>
        {t('loyalty.guest.confirmTitle')}
      </p>
      <p className="m-0 mt-1 font-mono text-xs text-ink-mute">{t('loyalty.guest.confirmNote')}</p>
      <button
        type="button"
        onClick={() =>
          dispatch(openAccountModal({ mode: 'register', redirectTo: `${pathname}${search}`, prefill }))
        }
        data-testid="guest-confirm-register"
        className="mt-3 min-h-[44px] rounded-btn-sm border-2 border-solid border-ink bg-mint px-4 font-display text-sm text-[var(--on-primary)]"
      >
        {t('loyalty.guest.register')}
      </button>
    </section>
  );
}
